import {
  deserialize,
  serialize,
} from '@/components/ui/TokenizedInput/serialization';
import type {
  Token,
  TokenDefinition,
} from '@/components/ui/TokenizedInput/types';

/**
 * Resolve filter text to human-readable display names.
 *
 * Parses the serialized filter text and:
 * 1. Replaces field keys with their labels (e.g., 'assignee_user_id' → 'Assigned user')
 * 2. Resolves entity IDs to display names (e.g., label ID '8' → 'Mounting')
 *
 * @example
 * // Input:  'labels contains: "8" assignee_user_id is: "42"'
 * // Output: 'Labels contains: "Mounting" Assigned user is: "John Doe"'
 */
export async function resolveFilterTextToHumanReadable(
  filterText: string,
  tokenDefinitions: TokenDefinition[],
): Promise<string> {
  if (!filterText?.trim()) {
    return '';
  }

  const parsed = deserialize(filterText);

  // Resolve all tokens in parallel for better performance
  const resolvedTokens = await Promise.all(
    parsed.tokens.map(async (token): Promise<Token> => {
      // Token.field matches TokenDefinition.key
      const def = tokenDefinitions.find((d) => d.key === token.field);

      // Start with current token values
      let resolvedField = token.field;
      let resolvedValue = token.value;

      // Replace field key with human-readable label
      if (def?.label) {
        resolvedField = def.label;
      }

      // Resolve foreign key values to display names
      if (def?.fetchOption && def?.getOptionLabel) {
        try {
          const entity = await def.fetchOption(token.value);
          resolvedValue = String(def.getOptionLabel(entity));
        } catch (error) {
          // Keep original value if resolution fails
          console.warn(
            `Failed to resolve filter value: ${token.field}=${token.value}`,
            error,
          );
        }
      }

      return {
        ...token,
        field: resolvedField,
        value: resolvedValue,
      };
    }),
  );

  return serialize({
    tokens: resolvedTokens,
    partial: null,
    remainder: parsed.remainder,
  });
}
